import { Popover } from '@headlessui/react';
import { mainClient } from 'apollo/client';
import { DetailProjectPageClientContext } from 'components/desktop/detail-project';
import SharePostPopover from 'components/desktop/detail/main-section/main-section-right/creator-information/share-post-popover';
import FriendActions from 'components/desktop/shared/friend-actions';
import SaveProjectModal from 'components/desktop/shared/save-project-modal';
import ToastStack from 'components/desktop/shared/toast-stack';
import {
  IconCalendarWeekFill,
  IconEmailFill,
  IconHeart,
  IconHeartOutline,
  IconPhone,
  IconShare,
} from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import ModalWrapper from 'components/shared/modal-wrapper';
import { SITE_MAP } from 'globalConstants';
import { GET_DETAIL_PROJECTS_ATMNHA } from 'graphql/main/queries';
import { useModal, useToast } from 'hooks';
import Image from 'next/image';
import Link from 'next/link';
import { DetailProjectPageContext } from 'pages/du-an/[chi-tiet-du-an]';
import { Fragment, MouseEvent, useContext, useState } from 'react';
import PriceListModal from './price-list-modal';
import RequestAdviseModal from './request-advise-modal';

const SupporterInformation = () => {
  const { project } = useContext(DetailProjectPageContext).pageData ?? {};
  const { project: projectClient } = useContext(DetailProjectPageClientContext).pageData ?? {};
  const { currentUser } = useAuthenticationContext();
  const { toasts, addToast, removeToast } = useToast();
  const { showing: showingPriceListModal, toggle: togglePriceListModal } = useModal();
  const { showing: showingRequestAdviseModal, toggle: toggleRequestAdviseModal } = useModal();
  const { showing: showingSaveProjectModal, toggle: toggleSaveProjectModal } = useModal();
  const [showingPhoneNumber, setShowingPhoneNumber] = useState(false);
  const [phoneNumberCopied, setPhoneNumberCopied] = useState(false);
  const { supporterATMNha } = project ?? {};
  const { savedItem } = projectClient ?? {};
  const { id: supporterId, fullName, phones, avatar } = supporterATMNha?.[0] ?? {};
  const { originalUrl } = avatar ?? {};
  const isSaved = !!savedItem?.id;

  const copyPhoneNumber = () => {
    if (phones?.[0]) {
      navigator.clipboard.writeText(phones[0]);
      setPhoneNumberCopied(true);
      addToast('success', 'Sao chép số điện thoại thành công');
      const copyTimeout = setTimeout(() => {
        setPhoneNumberCopied(false);
        clearTimeout(copyTimeout);
      }, 2 * 1000);
    }
  };
  const saveProject = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    toggleSaveProjectModal();
  };

  return (
    <Fragment>
      <div id='supporter-information' className='rounded-[16px] bg-paper p-[24px] shadow-7'>
        <div className='py-[4px]'>
          <h3 className='text-[16px] font-[600] leading-[20px]'>Hỗ trợ dự án</h3>
        </div>
        <div className='mt-[16px] flex items-center space-x-[12px]'>
          <div className='relative h-[72px] min-w-[72px] overflow-hidden rounded-full'>
            <Image
              src={`${originalUrl ? originalUrl : '/images/default-avatar.svg'}`}
              alt='avatar'
              layout='fill'
              objectFit='cover'
              placeholder='blur'
              blurDataURL='/images/image-loader.svg'
            />
          </div>
          <div className='flex flex-1 flex-col space-y-[8px] overflow-hidden'>
            {fullName && (
              <Link
                href={`/${SITE_MAP.LIST_BROKER.INDEX}/${supporterId}`}
                className='truncate font-[600]'
              >
                {fullName}
              </Link>
            )}
            <span className='truncate text-[12px] leading-[15px] text-text2'>
              Hỗ trợ dự án 24/7
            </span>
          </div>
        </div>
        <div className='mt-[24px] flex flex-col space-y-[16px]'>
          <button
            className='flex h-[36px] items-center justify-center rounded-[8px] bg-secondary text-primary duration-[200ms] hover:bg-secondary-light'
            onClick={() => {
              if (!showingPhoneNumber) {
                setShowingPhoneNumber(true);
              } else {
                copyPhoneNumber();
              }
            }}
          >
            <IconPhone className='h-[16px] w-[16px] text-primary' />
            <span className='ml-[10px] font-[600]'>
              {showingPhoneNumber
                ? `${phones?.[0] ?? 'Không có'} . ${phoneNumberCopied ? 'Đã sao chép' : 'Sao chép'}`
                : 'Liên hệ ngay'}
            </span>
          </button>
          <button
            className='flex h-[36px] items-center justify-center rounded-[8px] bg-tertiary duration-[200ms] hover:bg-tertiary-light'
            onClick={togglePriceListModal}
          >
            <IconCalendarWeekFill className='h-[16px] w-[16px]' />
            <span className='ml-[10px] font-[600]'>Đăng ký nhận bảng giá</span>
          </button>
          <ModalWrapper showing={showingPriceListModal}>
            <PriceListModal onClose={togglePriceListModal} />
          </ModalWrapper>
          <button
            className='flex h-[36px] items-center justify-center rounded-[8px] bg-tertiary duration-[200ms] hover:bg-tertiary-light'
            onClick={toggleRequestAdviseModal}
          >
            <IconEmailFill className='h-[20px] w-[20px]' />
            <span className='ml-[10px] font-[600]'>Yêu cầu tư vấn</span>
          </button>
          <ModalWrapper showing={showingRequestAdviseModal}>
            <RequestAdviseModal onClose={toggleRequestAdviseModal} />
          </ModalWrapper>
          <FriendActions user={supporterATMNha?.[0]} />
          <div className='relative flex items-center justify-between space-x-[16px]'>
            {currentUser && (
              <Fragment>
                <button
                  type='button'
                  className='flex w-full items-center justify-center space-x-[6px] p-[4px]'
                  onClick={saveProject}
                >
                  <div
                    className={`flex h-[20px] w-[20px] items-center justify-center rounded-full ${
                      isSaved ? 'bg-primary' : 'border border-paper bg-[#000000]/[.2]'
                    }`}
                  >
                    {isSaved ? (
                      <IconHeart className='h-[12px] w-[12px] text-paper' />
                    ) : (
                      <IconHeartOutline className='h-[12px] w-[12px] text-paper' />
                    )}
                  </div>
                  <span className='text-[12px] font-[600] leading-[15px]'>Lưu dự án</span>
                </button>
                <div className='h-[24px] w-[1px] bg-text2' />
                <ModalWrapper showing={showingSaveProjectModal}>
                  <SaveProjectModal
                    project={projectClient}
                    onCompleteSaving={() =>
                      mainClient.refetchQueries({ include: [GET_DETAIL_PROJECTS_ATMNHA] })
                    }
                    onClose={toggleSaveProjectModal}
                  />
                </ModalWrapper>
              </Fragment>
            )}
            <Popover className='relative w-full'>
              {({ open }) => (
                <Fragment>
                  <Popover.Button
                    type='button'
                    className='flex w-full items-center justify-center space-x-[6px]'
                  >
                    <IconShare className='h-[20px] w-[20px] text-primary' />
                    <span className='text-[12px] font-[600] leading-[15px]'>Chia sẻ dự án</span>
                  </Popover.Button>
                  {
                    <div className={`${open ? 'block' : 'hidden'}`}>
                      <SharePostPopover />
                    </div>
                  }
                </Fragment>
              )}
            </Popover>
          </div>
        </div>
      </div>
      <ToastStack toasts={toasts} removeToast={removeToast} />
    </Fragment>
  );
};

export default SupporterInformation;
